import { ErrorMessage } from '@e3dc-react/shell/Elements/Error'
import { ListSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import React from 'react'
import { UseAttachedInstallers } from 'src/Libs/retrofit'
import useModals from 'src/Hooks/useModals'
import InstallersWithAccessContent from './InstallersWithAccessContent'

const useStyles = makeStyles()(theme => ({
  grantAccessContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
  },
}))

type Props = {
  installationId: string | undefined
  isInstallationDecommissioned: boolean
  attachedInstallersHookResult: UseAttachedInstallers
  refetchData: () => void
}

/**
 * "Installers with access" section for the Flow theme. Handles loading/error states and exposes the
 * grant-access action, delegating the table rendering to InstallersWithAccessContent.
 * @param installationId - installation id
 * @param isInstallationDecommissioned - whether the installation is decommissioned
 * @param attachedInstallersHookResult - result of the useAttachedInstallers hook
 * @param refetchData - function to refetch the installation data with attached installers
 */
const InstallersWithAccess: React.FC<Props> = ({ installationId, isInstallationDecommissioned, attachedInstallersHookResult, refetchData }) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const { data, isError, isLoading } = attachedInstallersHookResult
  const { openGrantInstallerAccessModal } = useModals()

  const onGrantAccessHandler = (): void => {
    if (!installationId) return
    openGrantInstallerAccessModal({
      installationId,
      installers: data,
      refetchData,
    })
  }

  return (
    <Box sx={{ marginLeft: -2 }}>
      {isLoading ? (
        <ListSkeleton length={3} />
      ) : isError ? (
        <ErrorMessage title={t('installation.details.info.installers-with-access-error')} />
      ) : (
        <InstallersWithAccessContent
          installers={data}
          installationId={installationId}
          isInstallationDecommissioned={isInstallationDecommissioned}
          refetchAttachedInstallers={refetchData}
        />
      )}
      {!isInstallationDecommissioned && (
        <Box className={classes.grantAccessContainer}>
          <Button disabled={!installationId || isLoading || isError} onClick={onGrantAccessHandler}>
            {t('installation.details.info.grant-access.button-title')}
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default InstallersWithAccess
